import { InputNumber, SelectPicker, TagPicker } from "rsuite";
import { MdDelete } from "react-icons/md";
import { ApiPatientTask } from "../core/apis/PatientTask";
import { useMemo, useState, useEffect, useRef } from "react";
import { Button } from "./button.component";
import { AddTaskSchema } from "../models/api";
import { UtilityDate } from "../utils/dateUtils";
import { Days } from "../config/constants";
import { PatientTaskUtils, TimeUtility } from "../utils";

export const AddTaskOnPatient = ({
  onChange,
  value,
  totalTime,
  totalTimeInSec,
  isDayCount,
  totalUnit,
  perUnitCost,
  isCostMeasureTypeHourly,
}: {
  onChange?: (tasks: AddTaskSchema[], inputTime: number) => void;
  value?: AddTaskSchema[];
  totalTime: number;
  totalTimeInSec?: number;
  serviceAuthStartDate?: Date;
  serviceAuthEndDate?: Date;
  isDayCount?: boolean;
  totalUnit: number;
  perUnitCost: number;
  isCostMeasureTypeHourly?: boolean;
}) => {
  const { data } = ApiPatientTask.GetAll.Hook();
  const [tasks, setTasks] = useState<AddTaskSchema[]>(
    value?.map((item) => ({ ...item, edited: true })) ?? []
  );

  const availableDays = Days.map((day) => ({
    label: day,
    value: day,
  }));

  const totalInputTimeInSec = useMemo(() => {
    const weeklyTaskRatioInMonth = 30 / 7;
    return tasks.reduce((acc, curr) => {
      if (curr.hasManualTimeChange) {
        if (curr.isMonthly) {
          return acc + curr.secondsPerDay;
        } else {
          return acc + curr.secondsPerDay * weeklyTaskRatioInMonth;
        }
      } else {
        return acc + curr.secondsPerDay * 30;
      }
    }, 0);
  }, [tasks]);

  const tasksSelectItems = useMemo(
    () =>
      data
        ? data
            ?.filter((item) =>
              tasks.every((task) => task.taskSkey !== item.sKey)
            )
            ?.map((task) => ({
              label: task.displayName ?? task.name,
              value: task.sKey,
            }))
        : [],
    [data, tasks]
  );

  const handleAddTaskButtonClick = (type: "weekly" | "monthly" = "weekly") => {
    if (type === "monthly") {
      setTasks((prev) => [
        ...prev,
        {
          isMonthly: true,
          secondsPerDay: 0,
          totalTaskPerMonth: 1,
          hasManualTimeChange: false,
        },
      ]);
      return;
    }

    setTasks((prev) => [
      ...prev,
      {
        isMonthly: false,
        taskSkey: tasksSelectItems[0]?.value,
        daysPerWeek: !!isDayCount ? 7 : 0,
        minutesPerDay: 0,
        secondsPerDay: 0,
        weeklyAvailabilities: [],
      },
    ]);
  };

  const addTaskValue = <T extends keyof AddTaskSchema>(
    property: T,
    value: AddTaskSchema[T],
    givenIndex: number
  ) => {
    const modifiedTasks = tasks.map((task, index) => {
      if (index === givenIndex) {
        task[property] = value;
      }
      return task;
    });
    setTasks(modifiedTasks);
  };

  useEffect(() => {
    setTasks(value?.map((item) => ({ ...item, edited: true })) ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onChange && onChange(tasks, totalInputTimeInSec);
  }, [onChange, tasks, totalInputTimeInSec]);

  const totalTask = useMemo(
    () =>
      tasks.reduce((acc, task) => {
        return acc + (task.daysPerWeek || task.totalTaskPerMonth || 0);
      }, 0),
    [tasks]
  );

  useEffect(() => {
    const handleTaskChange = (prev: AddTaskSchema[]) => {
      let newTasks = PatientTaskUtils.updateTaskTime({
        tasks: prev,
        totalTimeInSeconds: totalTimeInSec || 0,
      });
      return [...newTasks];
    };
    setTasks((prev) => {
      return handleTaskChange(prev);
    });
  }, [totalTask, totalTime, totalTimeInSec]);

  const containerRef = useRef<HTMLDivElement>(null);
  const totalInputTimeInHour = TimeUtility.hourFormatter(totalInputTimeInSec, {
    valueType: "seconds",
    fractionDigits: 2,
  });
  const inputTimeInHour = TimeUtility.hourFormatter(totalTimeInSec ?? 0, {
    valueType: "seconds",
    fractionDigits: 2,
  });
  const haveUnequalTime =
    totalTask > 0 ? totalInputTimeInHour !== inputTimeInHour : false;

  return (
    <div>
      {!!totalUnit && (
        <div className="card shadow-sm p-3 mb-3">
          <div className="row g-0">
            <div className="col">
              <div className="d-flex align-items-center">
                <i className="bi bi-clock me-2"></i>
                <div>
                  <small className="text-muted">Total Time</small>
                  <div className="fw-bold">
                    {((totalUnit * 15) / 60).toFixed(2)} hrs
                  </div>
                </div>
              </div>
            </div>
            <div className="col">
              <div className="d-flex align-items-center">
                <i className="bi bi-currency-dollar me-2"></i>
                <div>
                  <small className="text-muted">
                    {isCostMeasureTypeHourly ? "Hourly Cost" : "Unit Cost"}
                  </small>
                  <div className="fw-bold">
                    ${isCostMeasureTypeHourly ? perUnitCost * 4 : perUnitCost}
                  </div>
                </div>
              </div>
            </div>
            <div className="col">
              <div className="d-flex align-items-center">
                <i className="bi bi-hourglass-split me-2"></i>
                <div>
                  <small className="text-muted">Remaining Time</small>
                  <div className={`fw-bold ${((Number(inputTimeInHour) - Number(totalInputTimeInHour))*60) < 0 ? 'text-danger' : ''}`}>
                    {((Number(inputTimeInHour) - Number(totalInputTimeInHour))*60).toFixed(2)} min
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card shadow-sm p-3">
        <div className="row mb-3">
          <div className="col d-flex gap-2">
            {data?.length && tasks.length < data.length && (
              <>
                <Button
                  name="+ Add Task"
                  onClick={() => handleAddTaskButtonClick()}
                  type="button"
                  className="btn-green-subtle m-1 transition"
                  disabled={haveUnequalTime}
                  title={haveUnequalTime ? "Please balance task times first" : "Add weekly task"}
                />
                <Button
                  name="+ Add Monthly Task"
                  onClick={() => handleAddTaskButtonClick("monthly")}
                  type="button"
                  className="btn-green-subtle m-1 transition"
                  disabled={haveUnequalTime}
                  title={haveUnequalTime ? "Please balance task times first" : "Add monthly task"}
                />
              </>
            )}
          </div>
          {totalInputTimeInSec > 0 && (
            <div className="col-auto">
              {totalInputTimeInHour > inputTimeInHour! && (
                <div className="alert alert-danger py-1 px-2 mb-0">
                  <small>
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    Total task time exceeds allocated time
                  </small>
                </div>
              )}
              {totalInputTimeInHour < inputTimeInHour! && (
                <div className="alert alert-warning py-1 px-2 mb-0">
                  <small>
                    <i className="bi bi-exclamation-circle me-1"></i>
                    Unused allocated time remaining
                  </small>
                </div>
              )}
            </div>
          )}
        </div>
        {tasks
          .map((task, index) => {
            const selectedTask = data?.find(
              (item) => item.sKey === task.taskSkey
            );
            const totalSeconds = (() => {
              if (task.isMonthly) {
                return task.secondsPerDay * 30;
              } else {
                return task.secondsPerDay * 7;
              }
            })();

            return (
              <div
                key={index}
                className="row border rounded m-1 mb-3 p-2 py-3 position-relative shadow-sm"
                ref={containerRef}
              >
                <div className="col-12 mb-1 d-flex justify-content-between align-items-center">
                  <span className={`badge ${task.isMonthly ? 'bg-warning' : 'bg-info'}`}>
                    {task.isMonthly ? 'Monthly Task' : 'Weekly Task'}
                  </span>
                  <button
                    className="btn btn-link text-danger p-0 ms-2"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to remove this task?')) {
                        setTasks((prev) => prev.filter((_, i) => i !== index));
                      }
                    }}
                    title="Remove Task"
                  >
                    <MdDelete size={24} />
                  </button>
                </div>
                <div className="col-md-3 px-1">
                  <p className="mb-1">
                    Add Task: <span className="text-red-300">*</span>
                  </p>
                  <SelectPicker
                    placeholder="Select a task"
                    placement="autoVertical"
                    data={
                      selectedTask
                        ? [
                            ...tasksSelectItems,
                            {
                              label:
                                selectedTask.displayName ?? selectedTask.name,
                              value: selectedTask.sKey,
                            },
                          ]
                        : tasksSelectItems
                    }
                    block
                    searchable={false}
                    onChange={(value: any) =>
                      setTasks((prev) =>
                        prev.map((task, i) =>
                          i === index
                            ? ({
                                ...task,
                                taskSkey: value,
                              } as AddTaskSchema)
                            : task
                        )
                      )
                    }
                    value={task.taskSkey}
                  />
                </div>

                <div className="col-md-7 px-1">
                  <p className="mb-1">
                    Number of{" "}
                    {task.isMonthly ? "Task per Month" : "Days per Week"}:{" "}
                    <span className="text-red-300">*</span>
                  </p>
                  <div className="row g-0">
                    {!task.isMonthly ? (
                      <>
                        <div className="col-3">
                          <SelectPicker
                            placeholder="Select Days"
                            placement="autoVertical"
                            data={[1, 2, 3, 4, 5, 6, 7].map((i) => ({
                              label: `${i} Day${i > 1 ? "s" : ""}`,
                              value: i,
                            }))}
                            block
                            searchable={false}
                            onChange={(value: any) => {
                              setTasks((prev) =>
                                prev.map((task, i) =>
                                  i === index
                                    ? ({
                                        ...task,
                                        daysPerWeek: value,
                                        weeklyAvailabilities: [],
                                      } as AddTaskSchema)
                                    : ({
                                        ...task,
                                        hasManualTimeChange: false,
                                      } as AddTaskSchema)
                                )
                              );
                            }}
                            value={task.daysPerWeek}
                          />
                        </div>
                        <div className={`col ${isDayCount ? "ps-2" : ""}`}>
                          <TagPicker
                            placeholder="Select available days"
                            aria-label="Select available days"
                            placement="autoVertical"
                            data={availableDays}
                            block
                            searchable={false}
                            cleanable
                            onChange={(value: any[]) => {
                              setTasks((prev) =>
                                prev.map((task, i) =>
                                  i === index
                                    ? ({
                                        ...task,
                                        daysPerWeek: value?.length ?? 0,
                                        weeklyAvailabilities: value,
                                        hasManualTimeChange: false,
                                      } as AddTaskSchema)
                                    : ({
                                        ...task,
                                        hasManualTimeChange: false,
                                      } as AddTaskSchema)
                                )
                              );
                            }}
                            value={task.weeklyAvailabilities}
                            trigger="Enter"
                          />
                        </div>
                      </>
                    ) : (
                      <InputNumber
                        value={task.totalTaskPerMonth}
                        min={1}
                        max={31}
                        onChange={(value) => {
                          addTaskValue("totalTaskPerMonth", Number(value), index);
                        }}
                      />
                    )}
                  </div>
                </div>

                <div className="col-md-2 px-1">
                  <p className="mb-1">
                    Minutes per {task.isMonthly ? "Month" : "Week"}:{" "}
                    <span className="text-red-300">*</span>
                  </p>
                  <InputNumber
                    value={
                      task.hasManualTimeChange
                        ? task.minutesPerDay
                        : PatientTaskUtils.formatSecondToMinute(totalSeconds)
                    }
                    onChange={(value) =>
                      setTasks((prev) =>
                        prev.map((task, i) =>
                          i === index
                            ? ({
                                ...task,
                                secondsPerDay: +value * 60 + 0,
                                minutesPerDay: value,
                                hasManualTimeChange: true,
                              } as AddTaskSchema)
                            : task
                        )
                      )
                    }
                    scrollable={false}
                    postfix="minutes"
                  />
                  {!!task?.secondsPerDay && (
                    <small className="m-0 p-0 mt-1 text-muted text">
                      {UtilityDate.minuteToHourString(
                        task.hasManualTimeChange
                          ? task.secondsPerDay / 60
                          : totalSeconds / 60
                      )}{" "}
                      per {task.isMonthly ? "Month" : "Week"}
                    </small>
                  )}
                </div>
              </div>
            );
          })
          .reverse()}
      </div>
    </div>
  );
};
